import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import type { AnyFieldDef } from '@resource-kit/model';
import { DEFAULT_STALE_TIME } from '@resource-kit/controllers/react';
import { ResourceDetailPanel } from './ResourceDetailPanel';
import { RowBusyProvider, useRowBusyRegistry } from '@resource-kit/controllers/react';
import { ResourceErrorBoundary } from './ResourceErrorBoundary';
import { PageContainer } from '../primitives/PageContainer';
import { LoadingPage } from '../primitives/Loading';
import { Card } from '../primitives/Card';
import { resourceKeys } from '@resource-kit/controllers';
import { useResourceKit } from '@resource-kit/core/react';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { createColumns } from '../columns';

interface ResourceDetailPageProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
}

interface ResourceDetailPageInnerProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  columns: ColumnDef<T>[];
}

export function ResourceDetailPage<T, F extends Record<string, AnyFieldDef>>({
  bundle,
}: ResourceDetailPageProps<T, F>) {
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => createColumns(bundle, { mode: 'detail' }), [bundle.host.slug]);
  return <ResourceDetailPageInner bundle={bundle} columns={columns} />;
}

export function ResourceDetailPageInner<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  columns,
}: ResourceDetailPageInnerProps<T, F>) {
  const { id } = useParams<{ id: string }>();
  const { t } = useResourceKit();
  const navigate = useNavigate();
  const location = useLocation();
  // #1417: `detail.actions` ride the shared per-row busy registry. The
  // standalone page has no sibling row cells, so it owns a LOCAL registry
  // (there is nothing to share with) — but the provider is still required for
  // the footer runner. A resource without `detail.actions` renders a footer
  // that never touches the registry, so this is inert for them.
  const rowBusy = useRowBusyRegistry();

  // The list route to return to after a terminal footer action. Explicit
  // `detail.listPath` wins; else strip the exact `/<id>` segment off this path
  // (a precise removal of the known param, not a shape-guessing regex).
  const idSegment = id ? `/${id}` : '';
  const strippedListPath =
    idSegment && location.pathname.endsWith(idSegment)
      ? location.pathname.slice(0, -idSegment.length) || '/'
      : location.pathname;
  const listRoute = bundle.host.detail?.listPath ?? strippedListPath;

  const query = useQuery({
    queryKey: resourceKeys(bundle.host.slug).detail(id),
    queryFn: () => {
      if (!bundle.host.api.get || !id) {
        throw new Error(`Missing detail API or id for ${bundle.host.slug}`);
      }
      return bundle.host.api.get(id);
    },
    enabled: !!id && !!bundle.host.api.get,
    staleTime: bundle.host.staleTime ?? DEFAULT_STALE_TIME,
  });

  if (query.isLoading) return <LoadingPage message={t('rk.resource.loadingRecord')} />;

  if (query.error) {
    return (
      <PageContainer>
        <Card className="p-6">
          <p className="text-sm text-destructive">
            {t('rk.ui.error')}: {query.error.message}
          </p>
        </Card>
      </PageContainer>
    );
  }

  if (!query.data) {
    return (
      <PageContainer>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">{t('rk.resource.recordNotFound')}</p>
        </Card>
      </PageContainer>
    );
  }

  return (
    <ResourceErrorBoundary
      fallbackTitle={t('rk.resource.errorLoading', {
        name: t(bundle.host.label, { defaultValue: bundle.host.label }),
      })}
    >
      <RowBusyProvider handle={rowBusy}>
        <ResourceDetailPanel
          item={query.data}
          bundle={bundle}
          columns={columns}
          embedded={false}
          // A terminal `afterAction: 'close'` action (e.g. delete) has no
          // drawer to close on a standalone page — return to the list route.
          // Prefer the resource's explicit `detail.listPath`; otherwise strip
          // the KNOWN `:id` segment off the current path (precise — it removes
          // exactly `/<id>`, not a guessed last segment).
          onClose={() => navigate(listRoute)}
        />
      </RowBusyProvider>
    </ResourceErrorBoundary>
  );
}
